import { useEffect } from 'react';

interface StructuredDataProps {
  type: 'organization' | 'service' | 'faq' | 'localBusiness';
  data?: any;
}

const StructuredData = ({ type, data }: StructuredDataProps) => {
  useEffect(() => {
    const getStructuredData = () => {
      switch (type) {
        case 'organization':
          return {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Ace Anchor",
            "alternateName": "Ace Anchor Australia",
            "url": "https://aceanchorau.com",
            "logo": "https://aceanchorau.com/logo.png",
            "description": "One-stop settlement solution for international students in Australia. Airport pickup, accommodation, TFN help, and 24/7 support.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Melbourne",
              "addressRegion": "VIC",
              "addressCountry": "AU"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+61-403-601-970",
              "contactType": "customer service",
              "email": "info@aceanchorau.com",
              "availableLanguage": ["English", "Hindi"]
            },
            "sameAs": [
              "https://www.instagram.com/aceanchorau",
              "https://www.facebook.com/aceanchorau"
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "ratingCount": "500",
              "bestRating": "5"
            }
          };
        
        case 'service':
          return {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Student Concierge Services",
            "provider": {
              "@type": "Organization",
              "name": "Ace Anchor"
            },
            "areaServed": {
              "@type": "City",
              "name": "Melbourne"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Student Settlement Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Smooth Landing",
                    "description": "Essential airport pickup and initial settlement services for international students",
                    "offers": {
                      "@type": "Offer",
                      "price": "279",
                      "priceCurrency": "AUD"
                    }
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Settle Smart",
                    "description": "Comprehensive settlement package including TFN, accommodation, and job search support",
                    "offers": {
                      "@type": "Offer",
                      "price": "449",
                      "priceCurrency": "AUD"
                    }
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Premium Anchor",
                    "description": "Complete premium settlement with ongoing support and professional networking",
                    "offers": {
                      "@type": "Offer",
                      "price": "649",
                      "priceCurrency": "AUD"
                    }
                  }
                }
              ]
            }
          };
        
        case 'faq':
          return {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I book my slot?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Simply click on 'Reserve Your Spot' button for your preferred plan, fill out the booking form with your details and arrival date, and our team will contact you within 24 hours to confirm and finalize the arrangements."
                }
              },
              {
                "@type": "Question",
                "name": "Can my parents track updates?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely! We provide regular updates via WhatsApp and email to both students and their families. For Premium Anchor plan, we also offer weekly video updates specifically for families back home, ensuring complete transparency and peace of mind."
                }
              },
              {
                "@type": "Question",
                "name": "What happens if my flight is delayed?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No worries! We monitor all flight statuses in real-time. If there's a delay, our team automatically adjusts the pickup schedule and keeps you and your family informed. Your safety and convenience are our top priorities."
                }
              },
              {
                "@type": "Question",
                "name": "Is the booking fee refundable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, we offer a full refund if you cancel at least 7 days before your arrival date. For cancellations within 7 days, a 50% refund is provided. We understand plans can change, and we try to be as flexible as possible."
                }
              },
              {
                "@type": "Question",
                "name": "Which cities do you operate in?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Currently, we offer our comprehensive services in Melbourne. As we're based in Melbourne and both founders studied here, we know the city inside out. We're planning to expand to other cities soon based on demand!"
                }
              },
              {
                "@type": "Question",
                "name": "What makes Ace Anchor different from other services?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We're founded by two former international students who understand exactly what you're going through. We don't just provide services - we provide empathy, understanding, and genuine care. Plus, our unique 'Home to Horizon' service ensures support from your doorstep to your new home in Melbourne."
                }
              }
            ]
          };
        
        case 'localBusiness':
          return {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Ace Anchor",
            "image": "https://aceanchorau.com/logo.png",
            "@id": "https://aceanchorau.com",
            "url": "https://aceanchorau.com",
            "telephone": "+61-403-601-970",
            "email": "info@aceanchorau.com",
            "priceRange": "$279-$649 AUD",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "",
              "addressLocality": "Melbourne",
              "addressRegion": "VIC",
              "postalCode": "",
              "addressCountry": "AU"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": -37.8136,
              "longitude": 144.9631
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday"
              ],
              "opens": "00:00",
              "closes": "23:59"
            },
            "sameAs": [
              "https://www.instagram.com/aceanchorau",
              "https://www.facebook.com/aceanchorau"
            ]
          };
        
        default:
          return null;
      }
    };

    const structuredData = getStructuredData();
    if (!structuredData) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    script.id = `structured-data-${type}`;
    
    // Remove existing script if present
    const existing = document.getElementById(`structured-data-${type}`);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(`structured-data-${type}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, data]);

  return null;
};

export default StructuredData;
